import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import {
  Modal,
  View,
  Text,
  Animated,
  Easing,
  Pressable,
  Platform,
  FlatList,
  StyleSheet,
  useWindowDimensions,
  type NativeScrollEvent,
  type NativeSyntheticEvent,
  type LayoutChangeEvent,
} from 'react-native';
import { BlurView } from 'expo-blur';
import { ChevronLeft, ChevronRight } from 'lucide-react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { WEB_HOVER_TRANSITION_STYLE } from '../../../src/shared/constants/animations';
import {
  RESULT_COPY,
  GAME_RESULT_DIMENSIONS,
} from './GameResultModal.constants';
import {
  getGameResultModalStyles,
  getGameResultModalLayoutStyles,
} from './GameResultModal.styles';
import { useSound } from '../../../src/shared/hooks/useSound';
import { SOUND_IDS } from '../../../src/shared/constants/sounds.constants';
import { GameResultSlide } from './GameResultSlide';
import type { GameResultItem, GameResultModalProps } from './gameResultModal.types';

export type { GameResultItem, GameResultModalProps } from './gameResultModal.types';

const nd = Platform.OS !== 'web';
const DESKTOP_MIN_WIDTH = 1024;

export const GameResultModal: React.FC<GameResultModalProps> = ({
  isVisible,
  onDismiss,
  results,
  hasOverflow = false,
}) => {
  const { playSound } = useSound();
  const { width: windowWidth, height: windowHeight } = useWindowDimensions();
  const screenW = Math.max(1, windowWidth);
  const insets = useSafeAreaInsets();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [bottomSectionH, setBottomSectionH] = useState(82);
  const flatListRef = useRef<FlatList<GameResultItem>>(null);

  // On mobile web, window.innerHeight doesn't account for browser toolbars (URL bar, nav bar).
  // visualViewport.height gives the actual visible area, preventing the bottom section from being clipped.
  const [visualViewportHeight, setVisualViewportHeight] = useState(windowHeight);
  useEffect(() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined') return;
    const update = () => setVisualViewportHeight(window.visualViewport?.height ?? window.innerHeight);
    update();
    window.visualViewport?.addEventListener('resize', update);
    return () => window.visualViewport?.removeEventListener('resize', update);
  }, []);
  const effectiveHeight = Platform.OS === 'web' ? visualViewportHeight : windowHeight;

  const item = results.length > 0 ? results[Math.min(currentIndex, results.length - 1)] : null;
  const isWon = item?.outcome === 'won';
  const isDraw = item?.outcome === 'draw';
  const hasMultiple = results.length > 1;
  const showDesktopArrows = Platform.OS === 'web' && hasMultiple && windowWidth >= DESKTOP_MIN_WIDTH;
  const cardWidth = Math.min(screenW - 32, GAME_RESULT_DIMENSIONS.CARD_W);

  const topPad = insets.top + 12;
  const bottomPad = insets.bottom + (Platform.OS === 'web' ? 20 : 32);
  const slideHeight = Math.max(effectiveHeight - topPad - bottomPad - bottomSectionH, 200);

  const onBottomLayout = useCallback(
    (e: LayoutChangeEvent) => setBottomSectionH(e.nativeEvent.layout.height),
    [],
  );

  const layoutStyles = useMemo(() => getGameResultModalLayoutStyles(screenW), [screenW]);
  const styles = useMemo(() => getGameResultModalStyles(insets), [insets]);

  const overlayOpacity = useRef(new Animated.Value(0)).current;
  const contentOpacity = useRef(new Animated.Value(0)).current;
  const titleScale = useRef(new Animated.Value(0.80)).current;
  const cardTranslateY = useRef(new Animated.Value(70)).current;
  const coinScale = useRef(new Animated.Value(0)).current;
  const ctaOpacity = useRef(new Animated.Value(0)).current;
  const ctaActiveRef = useRef(false);

  const resetAll = useCallback(() => {
    ctaActiveRef.current = false;
    overlayOpacity.setValue(0);
    contentOpacity.setValue(0);
    titleScale.setValue(0.80);
    cardTranslateY.setValue(70);
    coinScale.setValue(0);
    ctaOpacity.setValue(0);
  }, [overlayOpacity, contentOpacity, titleScale, cardTranslateY, coinScale, ctaOpacity]);

  useEffect(() => {
    if (!isVisible) {
      resetAll();
      setCurrentIndex(0);
      return;
    }

    Animated.timing(overlayOpacity, { toValue: 1, duration: 220, useNativeDriver: nd }).start();

    Animated.timing(contentOpacity, { toValue: 1, duration: 240, useNativeDriver: nd }).start(() => {
      Animated.stagger(60, [
        Animated.parallel([
          Animated.spring(titleScale, { toValue: 1, tension: 90, friction: 8, useNativeDriver: nd }),
          Animated.spring(cardTranslateY, { toValue: 0, tension: 90, friction: 9, useNativeDriver: nd }),
        ]),
        Animated.spring(coinScale, { toValue: 1, tension: 120, friction: 6, useNativeDriver: nd }),
      ]).start();

      // CTA: fade in, then pulse via recursive timing chain (Animated.loop resets value on start)
      Animated.timing(ctaOpacity, { toValue: 1, duration: 280, delay: 180, useNativeDriver: nd }).start(({ finished }) => {
        if (!finished) return;
        ctaActiveRef.current = true;
        const pulse = (toVal: number) => {
          if (!ctaActiveRef.current) return;
          Animated.timing(ctaOpacity, {
            toValue: toVal,
            duration: 960,
            easing: Easing.inOut(Easing.ease),
            useNativeDriver: nd,
          }).start(({ finished: f }) => {
            if (f) pulse(toVal === 0.50 ? 1 : 0.50);
          });
        };
        pulse(0.50);
      });
    });
  }, [isVisible, overlayOpacity, contentOpacity, titleScale, cardTranslateY, coinScale, ctaOpacity, resetAll]);

  useEffect(() => {
    if (!isVisible || results.length === 0) return;
    const idx = Math.min(currentIndex, results.length - 1);
    const outcome = results[idx]?.outcome;
    if (outcome === 'won') {
      playSound(SOUND_IDS.CHALLENGE_WIN);
    } else if (outcome === 'lost') {
      playSound(SOUND_IDS.CHALLENGE_LOST);
    }
  }, [isVisible, currentIndex, results, playSound]);

  const handleScroll = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      const offsetX = event.nativeEvent.contentOffset.x;
      const index = Math.round(offsetX / screenW);
      const clamped = Math.max(0, Math.min(index, results.length - 1));
      if (clamped !== currentIndex) setCurrentIndex(clamped);
    },
    [currentIndex, results.length, screenW],
  );

  const handleMomentumScrollEnd = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      const offsetX = event.nativeEvent.contentOffset.x;
      const index = Math.round(offsetX / screenW);
      setCurrentIndex(Math.max(0, Math.min(index, results.length - 1)));
    },
    [results.length, screenW],
  );

  const handleArrowNavigation = useCallback(
    (direction: 'prev' | 'next') => {
      if (!hasMultiple) return;
      const delta = direction === 'next' ? 1 : -1;
      const nextIndex = Math.max(0, Math.min(currentIndex + delta, results.length - 1));
      if (nextIndex === currentIndex) return;
      flatListRef.current?.scrollToIndex({ index: nextIndex, animated: true });
      setCurrentIndex(nextIndex);
    },
    [currentIndex, hasMultiple, results.length],
  );

  const getItemLayout = useCallback(
    (_: unknown, index: number) => ({
      length: screenW,
      offset: screenW * index,
      index,
    }),
    [screenW],
  );

  const renderItem = useCallback(
    ({ item: slideItem, index }: { item: GameResultItem; index: number }) => (
      <GameResultSlide
        item={slideItem}
        slideWidth={screenW}
        slideHeight={slideHeight}
        cardWidth={cardWidth}
        titleScale={titleScale}
        cardTranslateY={cardTranslateY}
        coinScale={coinScale}
        slideIndex={index}
        totalSlides={results.length}
      />
    ),
    [screenW, slideHeight, cardWidth, titleScale, cardTranslateY, coinScale, results.length],
  );

  if (!results.length) return null;

  return (
    <Modal
      visible={isVisible}
      transparent
      animationType="none"
      onRequestClose={onDismiss}
      statusBarTranslucent
      presentationStyle="overFullScreen"
    >
      <View style={styles.overlayPressable}>
        <Pressable style={StyleSheet.absoluteFillObject} onPress={onDismiss} />
        {Platform.OS !== 'web' && BlurView ? (
          <BlurView pointerEvents="none" intensity={40} style={styles.blurBg} />
        ) : null}
        <Animated.View
          pointerEvents="none"
          style={[styles.overlayBg, { opacity: overlayOpacity }]}
        />
        <Animated.View
          pointerEvents="box-none"
          style={[styles.contentWrap, { opacity: contentOpacity }]}
        >
          {hasMultiple ? (
            <FlatList
              ref={flatListRef}
              data={results}
              renderItem={renderItem}
              keyExtractor={(r, i) => r.challengeId ?? String(i)}
              horizontal
              pagingEnabled
              showsHorizontalScrollIndicator={false}
              accessibilityLabel="Game results — swipe to view more"
              focusable
              onScroll={handleScroll}
              onMomentumScrollEnd={handleMomentumScrollEnd}
              scrollEventThrottle={16}
              getItemLayout={getItemLayout}
              snapToInterval={screenW}
              decelerationRate="fast"
              style={[layoutStyles.contentList, { height: slideHeight }]}
              contentContainerStyle={layoutStyles.contentListContent}
            />
          ) : (
            <GameResultSlide
              item={results[0]}
              slideHeight={slideHeight}
              cardWidth={cardWidth}
              titleScale={titleScale}
              cardTranslateY={cardTranslateY}
              coinScale={coinScale}
              slideIndex={0}
              totalSlides={1}
            />
          )}

          {/* Bottom section */}
          <View style={styles.bottomSection} onLayout={onBottomLayout}>
            {showDesktopArrows && (
              <View style={styles.desktopArrowsRow}>
                <Pressable
                  onPress={() => handleArrowNavigation('prev')}
                  disabled={currentIndex === 0}
                  style={({ pressed, hovered }) => [
                    styles.desktopArrowButton,
                    Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
                    pressed && styles.desktopArrowButtonPressed,
                    hovered && Platform.OS === 'web' && styles.desktopArrowButtonHover,
                    currentIndex === 0 && styles.desktopArrowButtonDisabled,
                  ]}
                >
                  <ChevronLeft size={22} color="rgba(255,255,255,0.95)" />
                </Pressable>
                <Pressable
                  onPress={() => handleArrowNavigation('next')}
                  disabled={currentIndex === results.length - 1}
                  style={({ pressed, hovered }) => [
                    styles.desktopArrowButton,
                    Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
                    pressed && styles.desktopArrowButtonPressed,
                    hovered && Platform.OS === 'web' && styles.desktopArrowButtonHover,
                    currentIndex === results.length - 1 && styles.desktopArrowButtonDisabled,
                  ]}
                >
                  <ChevronRight size={22} color="rgba(255,255,255,0.95)" />
                </Pressable>
              </View>
            )}
            {hasMultiple && !showDesktopArrows && (
              <View style={styles.dotsRow}>
                {results.map((_, i) => (
                  <View
                    key={i}
                    style={[styles.dot, i === currentIndex ? styles.dotActive : styles.dotInactive]}
                  />
                ))}
              </View>
            )}
            {hasOverflow && (
              <Text style={styles.overflowHint}>{RESULT_COPY.OVERFLOW_HINT_TEXT}</Text>
            )}
            <Animated.View style={{ opacity: ctaOpacity }}>
              <Pressable
                onPress={onDismiss}
                style={({ pressed, hovered }) => [
                  styles.ctaPressable,
                  Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
                  pressed && styles.ctaPressablePressed,
                  hovered && Platform.OS === 'web' && styles.ctaPressableHover,
                ]}
              >
                <Text style={styles.ctaText}>
                  {isDraw
                    ? RESULT_COPY.CTA_DRAW
                    : isWon
                      ? RESULT_COPY.CTA_WON
                      : RESULT_COPY.CTA_LOST}
                </Text>
              </Pressable>
            </Animated.View>
          </View>
        </Animated.View>
      </View>
    </Modal>
  );
};
